<script lang="ts">
  import { Dialog } from 'bits-ui'
  import type { User, Permission } from '../lib/types'

  let users = $state<User[]>([])
  let loading = $state(true)
  let error = $state('')

  let showNewUserModal = $state(false)
  let newUserEmail = $state('')
  let newUserName = $state('')
  let newUserPassword = $state('')
  let newUserPermissions = $state<Set<Permission>>(new Set())
  let newUserError = $state('')
  let newUserLoading = $state(false)

  const ALL_PERMISSIONS: Permission[] = [
    'can_annotate',
    'can_chat',
    'can_analyze_videos',
    'can_manage_videos',
    'can_manage_users',
    'can_all',
  ]

  const PERMISSION_LABELS: Record<Permission, string> = {
    can_annotate: 'Annotate',
    can_chat: 'Chat',
    can_analyze_videos: 'Analyze',
    can_manage_videos: 'Manage Videos',
    can_manage_users: 'Manage Users',
    can_all: 'Super Admin',
  }

  const PERMISSION_COLORS: Record<Permission, string> = {
    can_annotate: '#8b5cf6',
    can_chat: '#3b82f6',
    can_analyze_videos: '#f59e0b',
    can_manage_videos: '#22c55e',
    can_manage_users: '#f97316',
    can_all: '#ef4444',
  }

  $effect(() => {
    fetchUsers()
  })

  async function fetchUsers() {
    loading = true
    error = ''
    try {
      const response = await fetch('/api/users', { credentials: 'same-origin' })
      if (response.ok) {
        users = await response.json()
      } else if (response.status === 403) {
        error = 'You do not have permission to manage users'
      } else {
        error = 'Failed to load users'
      }
    } catch (e) {
      error = 'Network error'
    }
    loading = false
  }

  async function updateUser(user: User, permissions: string[]) {
    try {
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ permissions }),
      })

      if (response.ok) {
        const updated = await response.json()
        users = users.map(u => u.id === updated.id ? updated : u)
      } else {
        alert('Failed to update user')
      }
    } catch (e) {
      alert('Network error')
    }
  }

  function togglePermission(user: User, permission: Permission) {
    const currentPermissions = [...user.permissions]
    const hasPermission = currentPermissions.includes(permission)

    let newPermissions: string[]
    if (hasPermission) {
      newPermissions = currentPermissions.filter(p => p !== permission)
    } else {
      newPermissions = [...currentPermissions, permission]
    }

    updateUser(user, newPermissions)
  }

  function openNewUserModal() {
    newUserEmail = ''
    newUserName = ''
    newUserPassword = ''
    newUserPermissions = new Set()
    newUserError = ''
    showNewUserModal = true
  }

  function toggleNewUserPermission(permission: Permission) {
    const newSet = new Set(newUserPermissions)
    if (newSet.has(permission)) {
      newSet.delete(permission)
    } else {
      newSet.add(permission)
    }
    newUserPermissions = newSet
  }

  async function createUser(e: Event) {
    e.preventDefault()
    newUserError = ''
    newUserLoading = true

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          email: newUserEmail,
          name: newUserName,
          password: newUserPassword,
          permissions: Array.from(newUserPermissions),
        }),
      })

      if (response.ok) {
        const newUser = await response.json()
        users = [...users, newUser]
        showNewUserModal = false
      } else {
        const data = await response.json()
        newUserError = data.error || 'Failed to create user'
      }
    } catch (e) {
      newUserError = 'Network error'
    }

    newUserLoading = false
  }
</script>

<div class="studio-console">
  <div class="console-header">
    <div class="header-label">
      <div class="label-primary">USER MANAGEMENT</div>
      <div class="label-secondary">{users.length} CHANNELS</div>
    </div>
    <button class="add-channel-btn" onclick={openNewUserModal}>
      <span class="btn-icon">+</span>
      <span class="btn-label">ADD USER</span>
    </button>
  </div>

  {#if loading}
    <div class="status-message">
      <div class="status-indicator pulse"></div>
      <span>LOADING CHANNELS...</span>
    </div>
  {:else if error}
    <div class="status-message error">
      <div class="status-indicator"></div>
      <span>{error.toUpperCase()}</span>
    </div>
  {:else}
    <div class="channel-rack">
      {#each users as user, idx (user.id)}
        <div class="channel-strip" style="animation-delay: {idx * 0.05}s">
          <div class="channel-number">CH{String(idx + 1).padStart(2, '0')}</div>

          <div class="channel-info">
            <div class="channel-name">{user.name}</div>
            <div class="channel-email">{user.email}</div>
          </div>

          <div class="meter-section">
            <div class="meter-label">PERMISSIONS</div>
            <div class="permission-switches">
              {#each ALL_PERMISSIONS as permission}
                {@const hasPermission = user.permissions.includes(permission)}
                {@const disabled = user.permissions.includes('can_all') && permission !== 'can_all'}
                <button
                  class="permission-switch"
                  class:active={hasPermission}
                  class:disabled
                  style="--switch-color: {PERMISSION_COLORS[permission]}"
                  onclick={() => !disabled && togglePermission(user, permission)}
                  disabled={disabled}
                >
                  <div class="switch-led"></div>
                  <div class="switch-label">{PERMISSION_LABELS[permission]}</div>
                </button>
              {/each}
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<Dialog.Root bind:open={showNewUserModal}>
  <Dialog.Portal>
    <Dialog.Overlay class="modal-backdrop" />
    <Dialog.Content class="modal-panel">
      <Dialog.Close class="modal-close">&times;</Dialog.Close>

      <Dialog.Title class="modal-title">
        <div class="title-primary">CREATE USER</div>
        <div class="title-secondary">SET UP A NEW ACCOUNT</div>
      </Dialog.Title>

      <form onsubmit={createUser} class="modal-form">
        <div class="form-group">
          <label for="new-name" class="form-label">DISPLAY NAME</label>
          <input
            type="text"
            id="new-name"
            bind:value={newUserName}
            required
            autocomplete="name"
            class="form-input"
            placeholder="John Doe"
          />
        </div>

        <div class="form-group">
          <label for="new-email" class="form-label">EMAIL ADDRESS</label>
          <input
            type="email"
            id="new-email"
            bind:value={newUserEmail}
            required
            autocomplete="email"
            class="form-input mono"
            placeholder="user@example.com"
          />
        </div>

        <div class="form-group">
          <label for="new-password" class="form-label">PASSWORD</label>
          <input
            type="password"
            id="new-password"
            bind:value={newUserPassword}
            required
            autocomplete="new-password"
            class="form-input mono"
            placeholder="••••••••"
          />
        </div>

        <div class="form-group">
          <label class="form-label">INITIAL PERMISSIONS</label>
          <div class="permission-matrix">
            {#each ALL_PERMISSIONS as permission}
              <button
                type="button"
                class="permission-toggle"
                class:active={newUserPermissions.has(permission)}
                style="--toggle-color: {PERMISSION_COLORS[permission]}"
                onclick={() => toggleNewUserPermission(permission)}
              >
                <div class="toggle-led"></div>
                <div class="toggle-label">{PERMISSION_LABELS[permission]}</div>
              </button>
            {/each}
          </div>
        </div>

        {#if newUserError}
          <div class="form-error">
            <div class="error-indicator"></div>
            {newUserError}
          </div>
        {/if}

        <div class="modal-actions">
          <button
            type="button"
            onclick={() => showNewUserModal = false}
            class="modal-btn secondary"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={newUserLoading}
            class="modal-btn primary"
          >
            {newUserLoading ? 'CREATING...' : 'CREATE USER'}
          </button>
        </div>
      </form>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>

<style>
  .studio-console {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background:
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(255, 255, 255, 0.015) 2px,
        rgba(255, 255, 255, 0.015) 4px
      ),
      var(--color-surface-0);
    padding: 2rem;
  }

  .console-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .header-label {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .label-primary {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-primary);
    font-family: var(--font-mono);
  }

  /* Testing hook again */

  .label-secondary {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  .add-channel-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-strong);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    cursor: pointer;
    transition: all 0.1s;
  }

  .add-channel-btn:hover {
    background: var(--color-accent-primary);
    border-color: var(--color-accent-primary);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }

  .btn-icon {
    font-size: 1.25rem;
    line-height: 1;
  }

  .status-message {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
    font-family: var(--font-mono);
    font-size: 0.875rem;
    letter-spacing: 0.1em;
    color: var(--color-text-secondary);
  }

  .status-message.error {
    color: var(--color-accent-playhead);
  }

  .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-text-muted);
  }

  .status-indicator.pulse {
    background: var(--color-accent-primary);
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }

  .channel-rack {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .channel-strip {
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
    animation: slideIn 0.3s ease-out forwards;
    opacity: 0;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .channel-strip::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--color-border-strong), transparent);
  }

  .channel-number {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    padding: 0.25rem 0.5rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-subtle);
    width: fit-content;
  }

  .channel-info {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .channel-name {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .channel-email {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--color-text-muted);
    letter-spacing: 0.02em;
  }

  .meter-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .meter-label {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
  }

  .permission-switches {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .permission-switch {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-default);
    cursor: pointer;
    transition: all 0.05s;
    position: relative;
  }

  .permission-switch:hover:not(.disabled) {
    background: var(--color-surface-4);
    border-color: var(--color-border-strong);
  }

  .permission-switch.active {
    background: color-mix(in srgb, var(--switch-color) 12%, var(--color-surface-3));
    border-color: var(--switch-color);
  }

  .permission-switch.active .switch-led {
    background: var(--switch-color);
    box-shadow: 0 0 8px var(--switch-color), 0 0 2px var(--switch-color);
  }

  .permission-switch.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .switch-led {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-border-default);
    transition: all 0.05s;
    flex-shrink: 0;
  }

  .switch-label {
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: 0.05em;
    color: var(--color-text-secondary);
    text-transform: uppercase;
  }

  .permission-switch.active .switch-label {
    color: var(--color-text-primary);
  }

  /* Modal Styles */
  :global(.modal-backdrop) {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(4px);
    z-index: 50;
  }

  :global(.modal-panel) {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 51;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    background: var(--color-surface-1);
    border: 1px solid var(--color-border-strong);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  }

  :global(.modal-close) {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.75rem;
    color: var(--color-text-muted);
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.1s;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :global(.modal-close:hover) {
    color: var(--color-text-primary);
  }

  :global(.modal-title) {
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  :global(.modal-panel) .title-primary {
    font-family: var(--font-mono);
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-primary);
    margin-bottom: 0.25rem;
  }

  :global(.modal-panel) .title-secondary {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 500;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
  }

  :global(.modal-panel) .modal-form {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  :global(.modal-panel) .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  :global(.modal-panel) .form-label {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    color: var(--color-text-secondary);
  }

  :global(.modal-panel) .form-input {
    padding: 0.75rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-primary);
    font-size: 0.875rem;
    transition: all 0.1s;
  }

  :global(.modal-panel) .form-input.mono {
    font-family: var(--font-mono);
    letter-spacing: 0.02em;
  }

  :global(.modal-panel) .form-input:focus {
    outline: none;
    border-color: var(--color-accent-primary);
    box-shadow: 0 0 0 1px var(--color-accent-primary);
  }

  :global(.modal-panel) .form-input::placeholder {
    color: var(--color-text-muted);
  }

  :global(.modal-panel) .permission-matrix {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  :global(.modal-panel) .permission-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-default);
    cursor: pointer;
    transition: all 0.05s;
  }

  :global(.modal-panel) .permission-toggle:hover {
    background: var(--color-surface-4);
    border-color: var(--color-border-strong);
  }

  :global(.modal-panel) .permission-toggle.active {
    background: color-mix(in srgb, var(--toggle-color) 15%, var(--color-surface-3));
    border-color: var(--toggle-color);
  }

  :global(.modal-panel) .permission-toggle.active .toggle-led {
    background: var(--toggle-color);
    box-shadow: 0 0 8px var(--toggle-color);
  }

  :global(.modal-panel) .toggle-led {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-border-default);
    transition: all 0.05s;
    flex-shrink: 0;
  }

  :global(.modal-panel) .toggle-label {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 500;
    letter-spacing: 0.05em;
    color: var(--color-text-secondary);
    text-transform: uppercase;
  }

  :global(.modal-panel) .permission-toggle.active .toggle-label {
    color: var(--color-text-primary);
  }

  :global(.modal-panel) .form-error {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
    background: color-mix(in srgb, var(--color-accent-playhead) 10%, transparent);
    border: 1px solid var(--color-accent-playhead);
    color: var(--color-accent-playhead);
    font-family: var(--font-mono);
    font-size: 0.75rem;
  }

  :global(.modal-panel) .error-indicator {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-accent-playhead);
    flex-shrink: 0;
  }

  :global(.modal-panel) .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 0.5rem;
  }

  :global(.modal-panel) .modal-btn {
    padding: 0.625rem 1.5rem;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    cursor: pointer;
    transition: all 0.1s;
    border: 1px solid;
  }

  :global(.modal-panel) .modal-btn.secondary {
    background: var(--color-surface-3);
    border-color: var(--color-border-default);
    color: var(--color-text-secondary);
  }

  :global(.modal-panel) .modal-btn.secondary:hover {
    background: var(--color-surface-4);
    border-color: var(--color-border-strong);
    color: var(--color-text-primary);
  }

  :global(.modal-panel) .modal-btn.primary {
    background: var(--color-accent-primary);
    border-color: var(--color-accent-primary);
    color: white;
  }

  :global(.modal-panel) .modal-btn.primary:hover:not(:disabled) {
    background: var(--color-accent-hover);
    border-color: var(--color-accent-hover);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
  }

  :global(.modal-panel) .modal-btn.primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
